﻿import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Badge,
    Button,
    Col,
    Container,
    Input,
    ListGroup,
    ListGroupItem,
    Row
} from 'reactstrap';
import {
    FaFileAlt,
    FaLink,
    FaPaperPlane,
    FaQuestionCircle
} from 'react-icons/fa';
import {
    CitationInfo,
    CitationType,
    MessageContent,
    MessageContentPartKind,
    MessageContentUpdateType,
    MessageRole,
    MessageStatusUpdateType,
    RunStepUpdateType,
    RunUpdateType,
    StreamingUpdate,
    StreamingUpdateReason,
    ThreadMessage
} from './types';
import getMessageContentPartKind from './getMessageContentPartKind';
import MarkdownViewer from '../MarkdownViewer';
import replaceCitationPlaceholders from './replaceCitationPlaceholders';
import { Components } from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { ThreadContext } from './ThreadContext.tsx';
import { useMutation } from 'react-query';
import usePostCreateThread from '../../api/usePostCreateThread.ts';
import usePostThreadHistory from '../../api/usePostThreadHistory.ts';
import useSendMessage from '../../api/useSendMessage.ts';
import handleServerSideEvents from './handleServerSideEvents.ts';
import { useEffectOnce } from '../../lib/useEffectOnce.ts';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import { toast } from 'react-toastify';
import useBotPagesConfig from '../../hooks/useBotPagesConfig.ts';
import useLocalStorage from '../../lib/useLocalStorage.ts';
import classNames from 'classnames';

const maxMessageLength = 500; // Maximum length for a message
declare global {
    interface Window {
        downloadCapturedUpdates?: () => void;
        toggleCaptureUpdates?: () => void;
        captureUpdates?: boolean;
    }
}

interface KnowledgeAssistantProps {
    initialQuery?: string;
}

const KnowledgeAssistant: React.FC<KnowledgeAssistantProps> = ({
    initialQuery
}) => {
    const { title: configTitle } = useBotPagesConfig();

    const { threadId, setThreadId, files, setFiles } =
        useContext(ThreadContext);

    const navigate = useNavigate();

    const [messages, setMessages] = useState<ThreadMessage[]>([]);

    const [inputText, setInputText] = useState<string>('');
    const [isAssistantTyping, setIsAssistantTyping] = useState<boolean>(false);

    const [conversationReady, setConversationReady] = useState<boolean>(false);
    const hasSentInitialQueryRef = useRef<boolean>(false);

    const [capturedUpdates, setCapturedUpdates] = useState<string[]>([]);

    const [citations, setCitations] = useState<CitationInfo[]>([]);

    const postThreadHistory = usePostThreadHistory();
    const postCreateThread = usePostCreateThread();

    const postThreadHistoryMutation = useMutation(postThreadHistory);
    const postCreateThreadMutation = useMutation(postCreateThread);

    const conversationHistoryRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (conversationHistoryRef.current) {
            conversationHistoryRef.current.scrollTop =
                conversationHistoryRef.current.scrollHeight;
        }
    }, [messages, isAssistantTyping]);

    const [captureUpdates, setCaptureUpdates] = useLocalStorage(
        'captureUpdates',
        false
    );

    // On Completion Update
    const onStreamingUpdate = useCallback(
        (streamingUpdate: StreamingUpdate) => {
            if (captureUpdates) {
                const timestamp = new Date().toISOString();
                setCapturedUpdates((prev) => [
                    ...prev,
                    `[${timestamp}] ${JSON.stringify(streamingUpdate, null, 2)}`
                ]);
            }

            const updateKind = streamingUpdate.updateKind;

            setIsAssistantTyping(true);

            if (updateKind === StreamingUpdateReason.Done) {
                setIsAssistantTyping(false);
            } else if (updateKind === StreamingUpdateReason.Error) {
                setIsAssistantTyping(false);
                toast.error(
                    'An error occurred, please try refreshing the page or asking the question again.'
                );
            } else if (updateKind === StreamingUpdateReason.RunCreated) {
                const runUpdate = streamingUpdate as RunUpdateType;
                const { threadId } = runUpdate.value;
                setThreadId(threadId);
            } else if (
                updateKind === StreamingUpdateReason.MessageCreated ||
                updateKind === StreamingUpdateReason.MessageInProgress
            ) {
                const messageUpdate =
                    streamingUpdate as MessageStatusUpdateType;
                const newMessage = messageUpdate.value; // ThreadMessage

                setMessages((prevMessages) => {
                    const messageExists = prevMessages.some(
                        (msg) => msg.id === newMessage.id
                    );
                    if (!messageExists) {
                        return [...prevMessages, newMessage];
                    }
                    return prevMessages;
                });
            } else if (updateKind === StreamingUpdateReason.MessageUpdated) {
                const contentUpdate =
                    streamingUpdate as MessageContentUpdateType;
                const {
                    messageId,
                    text,
                    imageFileId,
                    imageDetail,
                    refusalUpdate,
                    textAnnotation
                } = contentUpdate;

                const contentPartKind =
                    getMessageContentPartKind(contentUpdate);

                setMessages((prevMessages) => {
                    let messageFound = false;

                    const updatedMessages = prevMessages.map((msg) => {
                        if (msg.id === messageId) {
                            messageFound = true;
                            const content = [...(msg.content || [])];

                            if (
                                contentPartKind === MessageContentPartKind.Text
                            ) {
                                const lastContent = content[content.length - 1];
                                if (
                                    lastContent &&
                                    lastContent.text !== undefined
                                ) {
                                    lastContent.text += text ?? '';

                                    if (textAnnotation) {
                                        lastContent.textAnnotations =
                                            lastContent.textAnnotations || [];
                                        lastContent.textAnnotations.push(
                                            textAnnotation
                                        );
                                    }
                                } else {
                                    content.push({
                                        text: text ?? '',
                                        textAnnotations: textAnnotation
                                            ? [textAnnotation]
                                            : []
                                    });
                                }
                            } else {
                                const newContent: MessageContent = {};
                                if (
                                    contentPartKind ===
                                    MessageContentPartKind.Image
                                ) {
                                    newContent.imageFileId = imageFileId;
                                    newContent.imageDetail = imageDetail;
                                } else if (
                                    contentPartKind ===
                                    MessageContentPartKind.Refusal
                                ) {
                                    newContent.refusal = refusalUpdate;
                                }
                                content.push(newContent);
                            }

                            return { ...msg, content };
                        }
                        return msg;
                    });

                    if (!messageFound) {
                        const newMessage: ThreadMessage = {
                            id: messageId,
                            createdAt: new Date().toISOString(),
                            threadId: threadId || '', // Ensure threadId is set
                            status: 'in_progress',
                            role: contentUpdate.role ?? MessageRole.Assistant,
                            content: []
                        };

                        if (contentPartKind === MessageContentPartKind.Text) {
                            newMessage.content.push({
                                text,
                                textAnnotations: textAnnotation
                                    ? [textAnnotation]
                                    : []
                            });
                        } else {
                            const newContent: MessageContent = {};
                            if (
                                contentPartKind === MessageContentPartKind.Image
                            ) {
                                newContent.imageFileId = imageFileId;
                                newContent.imageDetail = imageDetail;
                            } else if (
                                contentPartKind ===
                                MessageContentPartKind.Refusal
                            ) {
                                newContent.refusal = refusalUpdate;
                            }
                            newMessage.content.push(newContent);
                        }

                        return [...updatedMessages, newMessage];
                    }

                    return updatedMessages;
                });
            } else if (updateKind === StreamingUpdateReason.RunStepCompleted) {
                const runStepUpdate = streamingUpdate as RunStepUpdateType;

                if (
                    runStepUpdate.value.metadata &&
                    'citationsData' in runStepUpdate.value.metadata
                ) {
                    const citationsData = JSON.parse(
                        runStepUpdate.value.metadata['citationsData']
                    ) as CitationInfo[];

                    setCitations((prevCitations) => {
                        const existingCitationsMap = new Map(
                            prevCitations.map((citation) => [
                                citation.fileName,
                                citation
                            ])
                        );

                        citationsData.forEach((newCitation) => {
                            const existing = existingCitationsMap.get(
                                newCitation.fileName
                            );

                            if (existing) {
                                if (newCitation.score > existing.score) {
                                    existingCitationsMap.set(
                                        newCitation.fileName,
                                        newCitation
                                    );
                                }
                            } else {
                                existingCitationsMap.set(
                                    newCitation.fileName,
                                    newCitation
                                );
                            }
                        });

                        const nextCitations = Array.from(
                            existingCitationsMap.values()
                        ).sort((a, b) => b.score - a.score);

                        setFiles(
                            nextCitations.map((citation) => ({
                                fileId: citation.fileId,
                                fileName: citation.fileName,
                                score: citation.score
                            }))
                        );

                        return nextCitations;
                    });
                }
            } else if (updateKind === StreamingUpdateReason.MessageCompleted) {
                setIsAssistantTyping(false);

                const messageUpdate =
                    streamingUpdate as MessageStatusUpdateType;
                const completedMessage = messageUpdate.value; // ThreadMessage

                setMessages((prevMessages) =>
                    prevMessages.map((msg) =>
                        msg.id === completedMessage.id ? completedMessage : msg
                    )
                );
            }
        },
        [setMessages, setCapturedUpdates, setThreadId, setFiles, threadId]
    );

    const lineBufferRef = useRef<string>('');
    const onLineReceived = useCallback(
        (line: string) => {
            setLastUpdateTimestamp(Date.now());
            if (line.startsWith('event:') || line.startsWith('data:')) {
                if (lineBufferRef.current) {
                    handleServerSideEvents(
                        lineBufferRef.current,
                        onStreamingUpdate
                    );
                }

                if (line === 'event: done' || line == 'data: [DONE]') {
                    handleServerSideEvents(line, onStreamingUpdate);
                    return;
                }

                if (line === 'event: error' || line == 'data: [ERROR]') {
                    handleServerSideEvents(line, onStreamingUpdate);
                    return;
                }

                lineBufferRef.current = line;
            } else {
                lineBufferRef.current += line;
            }
        },
        [onStreamingUpdate]
    );

    const sendMessageQuery = useSendMessage(onLineReceived);

    const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState<number>(
        Date.now()
    );
    const TIMEOUT_MS = 20_000; // 20 seconds
    useEffect(() => {
        if (isAssistantTyping) {
            const interval = setInterval(() => {
                if (Date.now() - lastUpdateTimestamp > TIMEOUT_MS) {
                    // No new updates for a while and still "typing"
                    console.warn(
                        "No 'done' event received, attempting recovery..."
                    );
                    setIsAssistantTyping(false);

                    if (threadId) {
                        postThreadHistoryMutation.mutateAsync(
                            {
                                threadId,
                                files
                            },
                            {
                                onSuccess: (updatedMessages) => {
                                    if (updatedMessages) {
                                        setMessages(updatedMessages.messages);
                                        setFiles(updatedMessages.files);
                                    }
                                },
                                onError: (error) => {
                                    console.error(
                                        'Failed to refresh thread history:',
                                        error
                                    );
                                }
                            }
                        );
                    } else {
                        toast.error(
                            'Request timed out, please refresh and try again.'
                        );
                    }

                    clearInterval(interval);
                }
            }, 5000); // Check every 5 seconds
            return () => clearInterval(interval);
        }
    }, [
        isAssistantTyping,
        lastUpdateTimestamp,
        postThreadHistoryMutation,
        setFiles
    ]);

    useEffectOnce(() => {
        if (threadId) {
            postThreadHistoryMutation.mutateAsync(
                {
                    threadId,
                    files
                },
                {
                    onSuccess: (threadHistoryResponse) => {
                        if (threadHistoryResponse) {
                            setMessages(threadHistoryResponse.messages);
                            setCitations(threadHistoryResponse.files);
                        }
                    },
                    onError: (error: unknown) => {
                        toast.error(unknownErrorToString(error));
                    },
                    onSettled: () => {
                        setConversationReady(true);
                    }
                }
            );
        } else {
            setConversationReady(true);
        }
    });

    // Handle Send Message
    const sendMessage = useCallback(
        async (messageText: string) => {
            if (messageText.trim() === '') {
                return;
            }

            const userMessage: ThreadMessage = {
                assistantId: '',
                attachments: [],
                completedAt: '',
                createdAt: '',
                id: '',
                incompleteAt: '',
                incompleteDetails: undefined,
                metadata: undefined,
                runId: '',
                status: 'in_progress',
                threadId: threadId || '', // Ensure threadId is set
                role: MessageRole.User,
                content: [
                    {
                        text: messageText
                    }
                ]
            };

            setMessages((prevMessages) => [...prevMessages, userMessage]);

            let existingOrNewThreadId = threadId;
            if (!existingOrNewThreadId) {
                const createThreadResponse =
                    await postCreateThreadMutation.mutateAsync(
                        {},
                        {
                            onError: (error) => {
                                toast.error(
                                    'Unable to create a new thread: ' +
                                        unknownErrorToString(error)
                                );
                            }
                        }
                    );

                if (createThreadResponse) {
                    existingOrNewThreadId = createThreadResponse.threadId;
                    setThreadId(existingOrNewThreadId);
                    setFiles([]); // Initialize files as empty array
                }
            }

            if (existingOrNewThreadId) {
                await sendMessageQuery.mutateAsync({
                    threadId: existingOrNewThreadId,
                    message: messageText
                });
            }
        },
        [
            threadId,
            postCreateThreadMutation,
            sendMessageQuery,
            setThreadId,
            setFiles
        ]
    );

    // Send initial query if provided
    useEffect(() => {
        if (
            conversationReady &&
            initialQuery &&
            !hasSentInitialQueryRef.current
        ) {
            sendMessage(initialQuery);
            hasSentInitialQueryRef.current = true;

            // Remove the searchText query parameter from the URL
            const params = new URLSearchParams(window.location.search);
            params.delete('searchText');
            navigate(
                {
                    pathname: window.location.pathname,
                    search: params.toString()
                },
                { replace: true }
            );
        }
    }, [conversationReady, initialQuery, sendMessage, navigate]);

    useEffect(() => {
        if (!captureUpdates) {
            return;
        }
        const downloadCapturedUpdates = () => {
            const text = capturedUpdates.join('\n');
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'captured_updates.txt';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        };
        window.downloadCapturedUpdates = downloadCapturedUpdates;
        return () => {
            delete window.downloadCapturedUpdates;
        };
    }, [capturedUpdates, captureUpdates]);

    const toggleCaptureUpdates = useCallback(() => {
        setCaptureUpdates((prevState) => !prevState);
    }, [setCaptureUpdates]);

    useEffect(() => {
        window.toggleCaptureUpdates = toggleCaptureUpdates;
        return () => {
            delete window.toggleCaptureUpdates;
        };
    }, [toggleCaptureUpdates]);

    useEffect(() => {
        window.captureUpdates = captureUpdates;
    }, [captureUpdates]);

    const handleSendMessage = useCallback(() => {
        if (inputText.trim() === '') {
            return;
        }

        sendMessage(inputText);
        setInputText('');
    }, [inputText, sendMessage]);

    const customMarkdownComponents: Partial<Components> = {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        a: ({ href, children, node, title, ...props }) => {
            // Check if this is a citation link
            const isCitation = href && href.startsWith('#citation-');

            if (isCitation) {
                const fileId = href.replace('#citation-', '');

                const indexOfCitation = citations.findIndex(
                    (c) => c.fileId == fileId
                );
                const citation = citations[indexOfCitation];

                return (
                    <a
                        href='#'
                        onClick={(e) => {
                            e.preventDefault();
                            document
                                .getElementById(`Citation_${fileId}`)
                                ?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                });
                        }}
                        title={citation.title}
                        style={{ textDecoration: 'none', cursor: 'pointer' }}
                        {...props}
                    >
                        <Badge
                            pill
                            color='primary'
                            className='align-text-top'
                            style={{
                                cursor: 'pointer',
                                fontSize: '0.75rem',
                                padding: '0.25em 0.6em',
                                marginLeft: '0.2em',
                                marginRight: '0.2em'
                            }}
                        >
                            {indexOfCitation + 1}
                        </Badge>
                    </a>
                );
            }

            // Regular external link handling
            const isExternal =
                href && !href.startsWith('/') && !href.startsWith('#');
            return (
                <a
                    href={href}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    {...props}
                >
                    {children}
                </a>
            );
        }
    };

    const formFollowUpSeparator = '#↷';

    const isForm = (text: string): boolean => {
        if (!text) {
            return false;
        }
        const isCalcOnly = text.indexOf('calculator=') !== -1;

        if (isCalcOnly) {
            return true;
        }
        const parts = text.split(formFollowUpSeparator);
        if (parts.length !== 2) {
            return false;
        }

        return isForm(parts[1]);
    };

    const getQuestionFromFormFollowUp = (text: string) => {
        const parts = text.split(formFollowUpSeparator);
        if (parts.length !== 2) {
            return null;
        }

        return parts[0];
    };

    const askQuestion = (question: string) => {
        if (window.faqbot) {
            window.faqbot.open();
            window.faqbot.askQuestion(question);
        }
    };

    // This creates one of three follow up types:
    // - Static text if assistant is typing
    // - A link to ask the assistant the question
    // - A link to ask the bot the question (useful for forms)
    const FollowUps = ({ citation }: { citation: CitationInfo }) => {
        return citation.followUpQuestions.map((question, qIndex) => {
            const isFormCitation = isForm(question);
            return (
                <li key={qIndex}>
                    {isAssistantTyping ? (
                        <span>{question}</span>
                    ) : isFormCitation ? (
                        <>
                            <a
                                href='#'
                                onClick={(event) => (
                                    event.preventDefault(),
                                    askQuestion(question)
                                )}
                            >
                                {getQuestionFromFormFollowUp(question) ??
                                    question}{' '}
                            </a>
                            - 🤖
                        </>
                    ) : (
                        <a
                            href='#'
                            onClick={(event) => (
                                event.preventDefault(), sendMessage(question)
                            )}
                        >
                            {question}
                        </a>
                    )}
                </li>
            );
        });
    };

    const getIcon = (citationType: CitationType) => {
        switch (citationType) {
            case CitationType.Article:
                return FaFileAlt;
            case CitationType.Web:
                return FaLink;
            default:
                return FaQuestionCircle;
        }
    };

    const renderCitationHeaderAndIcon = (citation: CitationInfo) => {
        const Header = () => (
            <h5 className='d-inline-flex citation-title align-items-center'>
                {citation.title}
            </h5>
        );
        const CitationIcon = getIcon(citation.citationType);

        return (
            <>
                <div className='d-inline-flex'>
                    <CitationIcon
                        size={18}
                        className='me-2 text-secondary'
                        aria-label='Article'
                    />
                </div>
                <Header />
            </>
        );
    };

    const nonWebCitations =
        citations
            ?.map((c, i) => ({
                ...c,
                originalIndex: i
            }))
            .filter((c) => c.citationType !== CitationType.Web) ?? [];

    const webCitations =
        citations
            ?.map((c, i) => ({
                ...c,
                originalIndex: i
            }))
            .filter((c) => c.citationType === CitationType.Web) ?? [];

    return (
        <Container className='chat-page'>
            <Row className='chat-container'>
                <Col xs={12} lg={8}>
                    <div className='chat-box'>
                        <h4 className='citations-header'>
                            {configTitle || 'Help pages assistant'}
                        </h4>
                        <div
                            className='conversation-history'
                            ref={conversationHistoryRef}
                        >
                            <ListGroup>
                                {messages.map((message, index) => (
                                    <ListGroupItem
                                        key={index}
                                        className={`d-flex ${
                                            message.role === MessageRole.User
                                                ? 'justify-content-end'
                                                : 'justify-content-start'
                                        }`}
                                    >
                                        <div
                                            className={`message-bubble ${
                                                message.role ===
                                                MessageRole.User
                                                    ? 'bg-primary-hf text-white'
                                                    : 'bg-light text-dark'
                                            } rounded`}
                                            style={{
                                                maxWidth: '75%'
                                            }}
                                        >
                                            {message.content.map(
                                                (content, idx) => {
                                                    const kind =
                                                        getMessageContentPartKind(
                                                            content
                                                        );

                                                    switch (kind) {
                                                        case MessageContentPartKind.Image: {
                                                            const imagePart =
                                                                content;
                                                            return imagePart.imageUri ? (
                                                                <img
                                                                    key={idx}
                                                                    src={
                                                                        imagePart.imageUri
                                                                    }
                                                                    alt='Chat Image'
                                                                    style={{
                                                                        maxWidth:
                                                                            '100%'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <small
                                                                    key={idx}
                                                                >
                                                                    [Image]
                                                                </small>
                                                            );
                                                        }
                                                        case MessageContentPartKind.Refusal:
                                                            return (
                                                                <small
                                                                    key={idx}
                                                                    className='text-danger'
                                                                >
                                                                    {
                                                                        (
                                                                            content as {
                                                                                refusal: string;
                                                                            }
                                                                        )
                                                                            .refusal
                                                                    }
                                                                </small>
                                                            );
                                                        default:
                                                            return (
                                                                <MarkdownViewer
                                                                    key={idx}
                                                                    content={
                                                                        content.text
                                                                            ? replaceCitationPlaceholders(
                                                                                  content,
                                                                                  citations
                                                                              )
                                                                            : ''
                                                                    }
                                                                    components={
                                                                        customMarkdownComponents
                                                                    }
                                                                />
                                                            );
                                                    }
                                                }
                                            )}
                                        </div>
                                    </ListGroupItem>
                                ))}
                                {isAssistantTyping && (
                                    <ListGroupItem className='d-flex justify-content-start'>
                                        <div className='bg-light text-dark p-2 rounded'>
                                            <small>
                                                Assistant is typing...
                                            </small>
                                        </div>
                                    </ListGroupItem>
                                )}
                            </ListGroup>
                        </div>
                        <footer className='chat-input'>
                            <Input
                                className='chat-textarea'
                                type='textarea'
                                value={inputText}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= maxMessageLength) {
                                        setInputText(value);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === 'Enter' &&
                                        !e.shiftKey &&
                                        inputText.length <= maxMessageLength
                                    ) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                placeholder='Type your message...'
                                maxLength={maxMessageLength}
                            />
                            <Button
                                color='primary'
                                className='chat-button'
                                onClick={handleSendMessage}
                                disabled={
                                    isAssistantTyping ||
                                    inputText.length > maxMessageLength
                                }
                            >
                                <FaPaperPlane />
                            </Button>
                            <div className='d-flex justify-content-between align-items-center mt-1'>
                                <small
                                    className={classNames({
                                        'd-none':
                                            inputText.length < maxMessageLength,
                                        'text-danger':
                                            inputText.length >= maxMessageLength
                                    })}
                                >
                                    {inputText.length}/{maxMessageLength}
                                </small>
                                {inputText.length > maxMessageLength && (
                                    <small className='text-danger ms-2'>
                                        Message too long (max {maxMessageLength}{' '}
                                        characters)
                                    </small>
                                )}
                            </div>
                        </footer>
                    </div>
                </Col>
                <Col xs={12} lg={4}>
                    <div className='citations-sidebar'>
                        <div className='citations-container'>
                            <h4 className='citations-header'>
                                Supporting information
                            </h4>
                            <div className='citations-list'>
                                {nonWebCitations.map((citation, index) => (
                                    <div
                                        id={`Citation_${citation.fileId}`}
                                        key={index}
                                        className={`citation-block ${
                                            citation.citationType ===
                                            CitationType.Article
                                                ? 'citation-clickable'
                                                : ''
                                        }`}
                                        onClick={() => {
                                            if (
                                                citation.citationType ===
                                                    CitationType.Article &&
                                                citation.url
                                            ) {
                                                window.open(
                                                    citation.url,
                                                    '_blank'
                                                );
                                            }
                                        }}
                                    >
                                        <div className='me-2'>
                                            <Badge
                                                color='primary'
                                                pill
                                                className='align-text-top'
                                                style={{
                                                    fontSize: '0.75rem',
                                                    padding: '0.25em 0.6em'
                                                }}
                                            >
                                                {citation.originalIndex + 1}
                                            </Badge>
                                        </div>
                                        <div className='flex-grow-1'>
                                            <div className='d-flex'>
                                                {renderCitationHeaderAndIcon(
                                                    citation
                                                )}
                                            </div>
                                            <div className='citation-content'>
                                                {isForm(citation.content) ? (
                                                    <div className='d-flex justify-content-end'>
                                                        <a
                                                            title={`Ask the chatbot: ${citation.title}`}
                                                            href='#'
                                                            className='btn btn-outline-primary rounded-pill m-1'
                                                            onClick={(
                                                                event
                                                            ) => (
                                                                event.preventDefault(),
                                                                askQuestion(
                                                                    citation.title
                                                                )
                                                            )}
                                                        >
                                                            Click here
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <MarkdownViewer
                                                        content={
                                                            citation.content
                                                        }
                                                    />
                                                )}
                                            </div>

                                            {citation.citationType ===
                                                CitationType.QA &&
                                                citation.followUpQuestions &&
                                                citation.followUpQuestions
                                                    .length > 0 && (
                                                    <div className='follow-up-questions'>
                                                        <h6>
                                                            Follow-up Questions:
                                                        </h6>
                                                        <ul>
                                                            <FollowUps
                                                                citation={
                                                                    citation
                                                                }
                                                            />
                                                        </ul>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                ))}
                                {webCitations.length > 0 && (
                                    <div
                                        id={`Citation_urls`}
                                        key={999}
                                        className={`citation-block`}
                                    >
                                        <div className='flex-grow-1'>
                                            <div className='d-flex'>
                                                <FaLink
                                                    size={18}
                                                    className='me-2 text-secondary'
                                                    aria-label='Article'
                                                />
                                                <h5>URLs found</h5>
                                            </div>
                                        </div>
                                        <div className='citation-content'>
                                            <ul>
                                                {webCitations.map(
                                                    (citation, index) => (
                                                        <li key={index}>
                                                            <a
                                                                id={`Citation_${citation.fileId}`}
                                                                href={
                                                                    citation.url
                                                                }
                                                            >
                                                                {citation.url}
                                                            </a>
                                                            {
                                                                <Badge
                                                                    color='primary'
                                                                    pill
                                                                    className='align-text-top ms-2'
                                                                    style={{
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        padding:
                                                                            '0.25em 0.6em'
                                                                    }}
                                                                >
                                                                    {citation.originalIndex +
                                                                        1}
                                                                </Badge>
                                                            }
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row className='mt-1'>
                <Col>
                    <p className='opacity-50 text-center'>
                        <small>
                            AI can make mistakes. Check supporting information
                            to be sure.
                        </small>
                    </p>
                </Col>
            </Row>
        </Container>
    );
};

export default KnowledgeAssistant;
